import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Heart, Users, Award, Instagram, Facebook } from "lucide-react";
import FollowUsSection from "../components/FollowUsSection";
import Stats from "../components/Stats";
import useScrollToTop from "../hooks/useScrollToTop";
import { useAuthors } from "../api/hooks/useAuthors";
import { useStats } from "../api/hooks/useStats";
import { assetUrl } from "../api/client";
import { useAboutPageData } from "../api/hooks/useAboutPageData";

const About = () => {
  const { t, i18n } = useTranslation();
  useScrollToTop(); // Scroll to top on navigation
  const { data: aboutPageData } = useAboutPageData();

  // Get the current language content from aboutPageData
  const currentLanguage = useMemo(() => {
    if (!aboutPageData?.translations) return null;

    const currentLang = i18n.language || "en-US";
    const translation = aboutPageData.translations.find(
      (tr) => tr.languages_code === currentLang
    );

    // Fallback to English if current language not found
    return (
      translation ||
      aboutPageData.translations.find((tr) => tr.languages_code === "en-US")
    );
  }, [aboutPageData, i18n.language]);

  const {
    data: authorsData,
    isLoading: authorsLoading,
    error: authorsError,
  } = useAuthors();
  const authors = authorsData?.data || [];

  // Helper function to get author description in current language
  const getAuthorDescription = (author) => {
    if (!author?.translations) return null;

    const currentLang = i18n.language || "en-US";
    const translation = author.translations.find(
      (tr) => tr.languages_code === currentLang
    );

    // Fallback to English if current language not found
    return (
      translation?.description ||
      author.translations.find((tr) => tr.languages_code === "en-US")
        ?.description
    );
  };

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useStats();
  const stats = statsData?.data || [];

  const values = [
    {
      icon: Users,
      title: currentLanguage?.community_title || "-",
      description: currentLanguage?.community_content || "-",
    },
    {
      icon: Award,
      title: currentLanguage?.authenticity_title || "-",
      description: currentLanguage?.authenticity_content || "-",
    },
    {
      icon: Heart,
      title: currentLanguage?.passion_title || "-",
      description: currentLanguage?.passion_content || "-",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-golden to-amber">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {currentLanguage?.title || "-"}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-lightCream max-w-3xl mx-auto">
            {currentLanguage?.subtitle || "-"}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {currentLanguage?.our_mission_title || "-"}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {currentLanguage?.our_mission_content || "-"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-teal to-sage">
                      <value.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stats
            data={stats}
            gradient="from-teal to-sage"
            gridCols="grid-cols-2 md:grid-cols-4"
            isLoading={statsLoading}
            error={statsError}
            language={i18n.language}
          />
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {currentLanguage?.our_team_title || "-"}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {currentLanguage?.our_team_content || "-"}
            </p>
          </div>

          {authorsLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">{t("common.loading")}</p>
            </div>
          ) : authorsError ? (
            <div className="text-center py-8">
              <p className="text-red-600">
                {t("common.error")}: {authorsError.message}
              </p>
            </div>
          ) : authors.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No team members found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {authors.map((author) => (
                <Card key={author.id} className="text-center">
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <img
                        src={
                          author.avatar
                            ? assetUrl(author.avatar, {
                                width: 200,
                                height: 200,
                              })
                            : "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80"
                        }
                        alt={`${author.first_name} ${author.last_name}`}
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    </div>
                    <CardTitle className="text-xl">
                      {author.first_name} {author.last_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {getAuthorDescription(author) && (
                      <p className="text-gray-600 mb-4">
                        {getAuthorDescription(author)}
                      </p>
                    )}
                    <div className="flex justify-center space-x-4">
                      {author.instagram && (
                        <a
                          href={author.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-500 hover:text-pink-600 transition-colors"
                        >
                          <span className="sr-only">Instagram</span>
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {author.face && (
                        <a
                          href={author.face}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          <span className="sr-only">Facebook</span>
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            {currentLanguage?.our_story_title || "-"}
          </h2>
          <div className="text-lg text-gray-600 space-y-6">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  currentLanguage?.our_story_content
                    ?.replace(/\n\n/g, "</p><p>")
                    .replace(/\n/g, "<br>") || "-",
              }}
            />
          </div>
        </div>
      </section>

      {/* Follow Us / Social */}
      <FollowUsSection gradient="from-teal to-sage" />
    </div>
  );
};

export default About;
